import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    Search, Loader2, Sparkles, Users, MapPin, Star, ChevronRight,
    FileText, Send, Bot, Briefcase, Clock, Tag, Zap, Upload,
    ArrowRight, MessageSquare, CheckCircle2, AlertTriangle, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_COLORS = {
    strong: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    good: 'bg-blue-100 text-blue-800 border-blue-200',
    partial: 'bg-amber-100 text-amber-800 border-amber-200',
    weak: 'bg-stone-100 text-stone-600 border-stone-200',
};
const getScoreColor = (score) =>
    score >= 80 ? SCORE_COLORS.strong : score >= 60 ? SCORE_COLORS.good : score >= 40 ? SCORE_COLORS.partial : SCORE_COLORS.weak;
const getScoreLabel = (score) =>
    score >= 80 ? 'Strong' : score >= 60 ? 'Good' : score >= 40 ? 'Partial' : 'Weak';

// ─── Result Card ────────────────────────────────────────────────────────────

function CandidateCard({ candidate, showScore, navigate }) {
    const score = candidate.match_score;
    return (
        <div
            onClick={() => navigate(`/recruitment/talent-pool/${candidate.applicant_id}`)}
            className="bg-white rounded-xl border border-stone-200 p-4 hover:border-stone-300 hover:shadow-sm cursor-pointer transition-all group"
        >
            <div className="flex items-start gap-3">
                {/* Score */}
                {showScore && score !== undefined && (
                    <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 border ${getScoreColor(score)}`}>
                        <span className="text-sm font-bold">{score}</span>
                        <span className="text-[8px] font-medium uppercase">{getScoreLabel(score)}</span>
                    </div>
                )}
                {!showScore && (
                    <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-bold text-sm flex-shrink-0">
                        {(candidate.name || '?')[0]}
                    </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-stone-900">{candidate.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            candidate.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-500'
                        }`}>{candidate.status}</span>
                    </div>
                    {candidate.headline && (
                        <p className="text-xs text-stone-500 mt-0.5 truncate">{candidate.headline}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {candidate.city && (
                            <span className="text-[10px] text-stone-400 flex items-center gap-0.5">
                                <MapPin className="w-3 h-3" /> {candidate.city}{candidate.country ? `, ${candidate.country}` : ''}
                            </span>
                        )}
                        {candidate.availability && (
                            <span className="text-[10px] text-stone-400 flex items-center gap-0.5">
                                <Clock className="w-3 h-3" /> {candidate.availability.replace(/_/g, ' ')}
                            </span>
                        )}
                        {candidate.experience_count > 0 && (
                            <span className="text-[10px] text-stone-400 flex items-center gap-0.5">
                                <Briefcase className="w-3 h-3" /> {candidate.experience_count} role{candidate.experience_count !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    {/* Skills */}
                    {candidate.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {candidate.skills.slice(0, 6).map((s, i) => (
                                <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded">{s}</span>
                            ))}
                            {candidate.skills.length > 6 && (
                                <span className="text-[10px] text-stone-400">+{candidate.skills.length - 6}</span>
                            )}
                        </div>
                    )}
                    {/* Match reasoning */}
                    {candidate.match_reasoning && (
                        <p className="text-xs text-stone-500 mt-2 italic">{candidate.match_reasoning}</p>
                    )}
                    {/* Strengths & Gaps */}
                    {(candidate.strengths?.length > 0 || candidate.gaps?.length > 0) && (
                        <div className="flex gap-4 mt-2">
                            {candidate.strengths?.length > 0 && (
                                <div className="flex items-start gap-1">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-[10px] text-stone-500">{candidate.strengths.slice(0, 2).join(', ')}</span>
                                </div>
                            )}
                            {candidate.gaps?.length > 0 && (
                                <div className="flex items-start gap-1">
                                    <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-[10px] text-stone-500">{candidate.gaps.slice(0, 2).join(', ')}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 flex-shrink-0 mt-1" />
            </div>
        </div>
    );
}


// ─── Main Page ──────────────────────────────────────────────────────────────

export default function ATSMatchingWorkspace() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState('chat'); // chat | pd
    const [query, setQuery] = useState('');
    const [pdText, setPdText] = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState(null);
    const [criteria, setCriteria] = useState(null);
    const [searchHistory, setSearchHistory] = useState([]);
    const inputRef = useRef(null);

    const handleChatSearch = async () => {
        if (!query.trim()) return;
        setSearching(true);
        setResults(null);
        try {
            const res = await axios.post(`${API}/ats/matching-workspace/search`, {
                query: query.trim(),
                limit: 20,
            }, { headers: getAuthHeader() });
            setResults(res.data.results || []);
            setCriteria(res.data.criteria);
            setSearchHistory(prev => [{ query: query.trim(), time: new Date(), count: (res.data.results || []).length }, ...prev.slice(0, 9)]);
        } catch (err) { toast.error(err.response?.data?.detail || 'Search failed'); }
        finally { setSearching(false); }
    };

    const handlePDMatch = async () => {
        if (!pdText.trim()) return;
        setSearching(true);
        setResults(null);
        try {
            const res = await axios.post(`${API}/ats/matching-workspace/pd-match`, {
                position_description: pdText.trim(),
                limit: 20,
            }, { headers: getAuthHeader() });
            setResults(res.data.results || []);
            setCriteria(res.data.requirements);
        } catch (err) { toast.error(err.response?.data?.detail || 'Matching failed'); }
        finally { setSearching(false); }
    };

    const SUGGESTIONS = [
        "Senior React developer with TypeScript experience, available immediately",
        "Customer service reps who speak English and Tagalog, night shift",
        "Project managers with BPO experience in Manila",
        "Data analysts with SQL and Python skills, hybrid work",
        "Technical writers available within 2 weeks",
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Fast Matching
                </h1>
                <p className="text-sm text-stone-500 mt-1">Find talent instantly — describe what you need or paste a position description</p>
            </div>

            {/* Mode Toggle */}
            <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5 w-fit">
                <button onClick={() => { setMode('chat'); setResults(null); setCriteria(null); }}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                        mode === 'chat' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                    }`}>
                    <MessageSquare className="w-4 h-4" /> Chat Search
                </button>
                <button onClick={() => { setMode('pd'); setResults(null); setCriteria(null); }}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                        mode === 'pd' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                    }`}>
                    <FileText className="w-4 h-4" /> PD Upload
                </button>
            </div>

            {/* ── Chat Search Mode ── */}
            {mode === 'chat' && (
                <div className="space-y-4">
                    {/* Search Input */}
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                                    Describe the talent you need
                                </label>
                                <textarea
                                    ref={inputRef}
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSearch(); } }}
                                    placeholder='e.g. "React developers with 3+ years experience in Manila, available immediately"'
                                    rows={2}
                                    className="w-full px-4 py-3 text-sm border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none"
                                />
                            </div>
                            <Button onClick={handleChatSearch} disabled={searching || !query.trim()}
                                className="bg-stone-900 text-white hover:bg-stone-800 h-11 px-6">
                                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Search</>}
                            </Button>
                        </div>

                        {/* Quick Suggestions */}
                        {!results && (
                            <div className="mt-3">
                                <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1.5">Try these</p>
                                <div className="flex flex-wrap gap-2">
                                    {SUGGESTIONS.map((s, i) => (
                                        <button key={i} onClick={() => { setQuery(s); setTimeout(() => inputRef.current?.focus(), 100); }}
                                            className="px-3 py-1.5 text-xs bg-stone-50 border border-stone-200 rounded-lg text-stone-600 hover:bg-stone-100 hover:border-stone-300 transition-colors text-left">
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Search History */}
                    {!results && searchHistory.length > 0 && (
                        <div className="bg-white rounded-xl border border-stone-200 p-4">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Recent Searches</h3>
                            <div className="space-y-1">
                                {searchHistory.map((h, i) => (
                                    <button key={i} onClick={() => { setQuery(h.query); handleChatSearch(); }}
                                        className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-stone-50 transition-colors">
                                        <Search className="w-3 h-3 text-stone-400" />
                                        <span className="text-sm text-stone-700 truncate flex-1">{h.query}</span>
                                        <span className="text-[10px] text-stone-400">{h.count} results</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── PD Upload Mode ── */}
            {mode === 'pd' && (
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-stone-200 p-4">
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                            Position Description
                        </label>
                        <textarea
                            value={pdText}
                            onChange={e => setPdText(e.target.value)}
                            placeholder="Paste the full position description here. The AI will extract requirements and find matching talent..."
                            rows={8}
                            className="w-full px-4 py-3 text-sm border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none"
                        />
                        <div className="flex items-center justify-between mt-3">
                            <p className="text-[10px] text-stone-400">
                                <Sparkles className="w-3 h-3 inline mr-0.5" />
                                AI will extract skills, experience level, location preferences, and more
                            </p>
                            <Button onClick={handlePDMatch} disabled={searching || !pdText.trim()}
                                className="bg-stone-900 text-white hover:bg-stone-800">
                                {searching ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Matching...</> : <><Sparkles className="w-4 h-4 mr-1" /> Find Matches</>}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Loading State ── */}
            {searching && (
                <div className="flex flex-col items-center py-12">
                    <div className="relative">
                        <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center">
                            <Sparkles className="w-7 h-7 text-stone-400 animate-pulse" />
                        </div>
                    </div>
                    <p className="text-sm text-stone-500 mt-4 font-medium">
                        {mode === 'pd' ? 'Analyzing PD & scoring candidates...' : 'Searching talent pool...'}
                    </p>
                    <p className="text-xs text-stone-400 mt-1">This may take a moment for AI scoring</p>
                </div>
            )}

            {/* ── Extracted Criteria ── */}
            {criteria && !searching && (
                <div className="bg-stone-50 rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider flex items-center gap-1">
                            <Bot className="w-3.5 h-3.5" />
                            {mode === 'pd' ? 'Extracted Requirements' : 'Search Criteria'}
                        </h3>
                        <button onClick={() => setCriteria(null)} className="text-stone-400 hover:text-stone-600"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {/* Skills */}
                        {(criteria.skills || criteria.must_have_skills || []).map((s, i) => (
                            <span key={`s-${i}`} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{s}</span>
                        ))}
                        {(criteria.preferred_skills || []).map((s, i) => (
                            <span key={`p-${i}`} className="px-2 py-0.5 bg-violet-50 text-violet-700 text-xs rounded-full border border-violet-200">{s}</span>
                        ))}
                        {/* Keywords */}
                        {(criteria.keywords || []).map((k, i) => (
                            <span key={`k-${i}`} className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full">{k}</span>
                        ))}
                        {criteria.location && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-200"><MapPin className="w-3 h-3 inline mr-0.5" />{criteria.location || criteria.location_preference}</span>}
                        {criteria.location_preference && !criteria.location && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-200"><MapPin className="w-3 h-3 inline mr-0.5" />{criteria.location_preference}</span>}
                        {criteria.experience_level && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-200">{criteria.experience_level}</span>}
                        {(criteria.availability || criteria.work_type) && <span className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full">{criteria.availability || criteria.work_type}</span>}
                    </div>
                    {(criteria.explanation || criteria.summary) && (
                        <p className="text-xs text-stone-500 mt-2 italic">{criteria.explanation || criteria.summary}</p>
                    )}
                </div>
            )}

            {/* ── Results ── */}
            {results && !searching && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-stone-700">
                            {results.length} candidate{results.length !== 1 ? 's' : ''} found
                        </h3>
                        <Button variant="outline" size="sm" onClick={() => { setResults(null); setCriteria(null); }}>
                            New Search
                        </Button>
                    </div>

                    {results.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                            <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-sm text-stone-500">No matching candidates found</p>
                            <p className="text-xs text-stone-400 mt-1">Try broadening your search criteria</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {results.map(c => (
                                <CandidateCard
                                    key={c.applicant_id}
                                    candidate={c}
                                    showScore={mode === 'pd' || c.match_score !== undefined}
                                    navigate={navigate}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
